import type { SecretProvider } from '@fabric-harness/sdk';
import { databricksCredentials, databricksIdentity, type DatabricksPrincipal } from './identity.js';
import { createDatabricksNativeClients } from './native-sdk.js';

export interface DatabricksSecretsProviderOptions {
  host: string;
  principal: DatabricksPrincipal;
  fetchImpl?: typeof fetch;
  scope: string | ((name: string, tenantId?: string) => string | Promise<string>);
  keyForSecret?: (name: string) => string;
}

/** Databricks Secret Management adapter. Secret values are decoded only at runtime. */
export function databricksSecretsProvider(options: DatabricksSecretsProviderOptions): SecretProvider {
  const identity = databricksIdentity(options.principal);
  const client = createDatabricksNativeClients({
    host: options.host,
    credentials: databricksCredentials(options.principal),
    tokenProvider: identity,
    ...(options.fetchImpl ? { fetchImpl: options.fetchImpl } : {}),
  }).secrets;
  return {
    name: 'databricks-secrets',
    async resolve(ref, context) {
      const scope = typeof options.scope === 'function' ? await options.scope(ref.name, context?.tenantId) : options.scope;
      const key = options.keyForSecret?.(ref.name) ?? ref.name;
      if (!scope || !key) throw new Error('Databricks secret scope and key are required.');
      try {
        const response = await client.getSecret({ scope, key });
        return response.value === undefined
          ? undefined
          : Buffer.from([...response.value]).toString('utf8');
      } catch (error) {
        const status = (error as { httpStatusCode?: unknown }).httpStatusCode;
        if (status === 404) return undefined;
        const message = error instanceof Error ? error.message : String(error);
        if (/RESOURCE_DOES_NOT_EXIST|does not exist|404/iu.test(message)) return undefined;
        throw error;
      }
    },
  };
}
